<script setup lang="ts">
/**
 * BulkOpsModal — 1:1 порт docs/mockups/modal-bulk-ops.html (inbox-191).
 *
 * Size md. 4 действия (move / tag / archive / delete) выбираются radio-grid.
 * Под каждым раскрывается details-section с control'ом (select / chips /
 * нет ничего для archive / type-to-confirm для delete).
 *
 * Payload: { selectedCount: number, entityType: 'link'|'campaign'|... }
 * Emit: 'bulk-ops' { action, params }
 */
import { computed, ref } from 'vue';
import { useModalStore } from '@/stores/modal';
import UiModal from '@/components/ui/UiModal.vue';
import UiButton from '@/components/ui/UiButton.vue';
import UiBadge from '@/components/ui/UiBadge.vue';

const modals = useModalStore();

interface Payload { selectedCount: number; entityType: string }
const payload = computed<Payload>(() => {
  const raw = (modals.payload || {}) as Record<string, unknown>;
  return {
    selectedCount: typeof raw.selectedCount === 'number' ? raw.selectedCount : 24,
    entityType: typeof raw.entityType === 'string' ? raw.entityType : 'link',
  };
});

type Op = 'move' | 'tag' | 'archive' | 'delete';
const op = ref<Op>('move');

const campaign = ref('Q2 ретаргетинг · 142 ссылки');
const CAMPAIGNS = [
  '— Без кампании —',
  'Q2 ретаргетинг · 142 ссылки',
  'Promo Лето 26 · 38 ссылок',
  'Бренд-кампания · 91 ссылка',
];

interface Tag { name: string; color: string; selected: boolean }
const tags = ref<Tag[]>([
  { name: 'blog',   color: '#4f8cff', selected: false },
  { name: 'email',  color: '#b366ff', selected: false },
  { name: 'Q2',     color: '#4dd693', selected: true },
  { name: 'social', color: '#ff8b4d', selected: false },
]);
function toggleTag(t: Tag) { t.selected = !t.selected; }

const deleteConfirm = ref('');
const expectedConfirm = computed(() => `delete ${payload.value.selectedCount}`);
const canDelete = computed(() => deleteConfirm.value.trim() === expectedConfirm.value);

const submitting = computed(() => modals.pending);

function apply() {
  let params: Record<string, unknown> = {};
  if (op.value === 'move') params = { campaign: campaign.value };
  else if (op.value === 'tag') params = { tags: tags.value.filter((t) => t.selected).map((t) => t.name) };
  else if (op.value === 'delete' && !canDelete.value) return;
  modals.setPending(true);
  modals.emit('bulk-ops', { action: op.value, params, entityType: payload.value.entityType });
  modals.setPending(false);
  modals.close();
}
</script>

<template>
  <UiModal title="Массовые операции" size="md" :disable-close="submitting" @close="modals.close">
    <template #header>
      <div>
        <h2 class="modal-title">Массовые операции</h2>
        <p class="modal-sub">Выбрано <strong>{{ payload.selectedCount }} {{ payload.entityType === 'link' ? 'ссылок' : 'элементов' }}</strong>. Действие применится сразу к каждой.</p>
      </div>
    </template>

    <div class="op-grid" role="radiogroup" aria-label="Действие">
      <label :class="['op-card', { selected: op === 'move' }]">
        <input type="radio" v-model="op" value="move" />
        <span class="op-icon op-icon-blue">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>
        </span>
        <div class="op-text">
          <span class="op-title">Переместить в кампанию</span>
          <span class="op-desc">Сохранит статистику, обновит фильтры.</span>
        </div>
      </label>
      <label :class="['op-card', { selected: op === 'tag' }]">
        <input type="radio" v-model="op" value="tag" />
        <span class="op-icon op-icon-purple">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2"><path d="M20.59 13.41 13.42 20.58a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><circle cx="7" cy="7" r="1.2"/></svg>
        </span>
        <div class="op-text">
          <span class="op-title">Добавить тег</span>
          <span class="op-desc">Существующий или создать новый.</span>
        </div>
      </label>
      <label :class="['op-card', { selected: op === 'archive' }]">
        <input type="radio" v-model="op" value="archive" />
        <span class="op-icon op-icon-orange">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2"><polyline points="21 8 21 21 3 21 3 8"/><rect x="1" y="3" width="22" height="5"/><line x1="10" y1="12" x2="14" y2="12"/></svg>
        </span>
        <div class="op-text">
          <span class="op-title">Архивировать</span>
          <span class="op-desc">Редиректы продолжат работать, скрыты из списка.</span>
        </div>
      </label>
      <label :class="['op-card op-card-danger', { selected: op === 'delete' }]">
        <input type="radio" v-model="op" value="delete" />
        <span class="op-icon op-icon-red">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
        </span>
        <div class="op-text">
          <span class="op-title">Удалить</span>
          <span class="op-desc">Редиректы перестанут работать. Необратимо.</span>
        </div>
      </label>
    </div>

    <!-- Detail per op -->
    <section v-if="op === 'move'" class="op-detail">
      <label class="field-label" for="bulk-campaign">Целевая кампания</label>
      <select id="bulk-campaign" v-model="campaign" class="input">
        <option v-for="c in CAMPAIGNS" :key="c" :value="c">{{ c }}</option>
      </select>
    </section>

    <section v-else-if="op === 'tag'" class="op-detail">
      <label class="field-label">Теги</label>
      <div class="tag-chips">
        <button
          v-for="t in tags"
          :key="t.name"
          type="button"
          :class="['tag-chip', { selected: t.selected }]"
          @click="toggleTag(t)"
        >
          <span class="tag-dot" :style="{ background: t.color }"></span>{{ t.name }}
        </button>
        <button type="button" class="tag-chip tag-add" disabled>+ Новый тег</button>
      </div>
    </section>

    <section v-else-if="op === 'archive'" class="op-detail">
      <p class="archive-note">
        Архив доступен 90 дней; восстановление в любой момент. Редиректы продолжат работать.
      </p>
    </section>

    <section v-else class="op-detail danger">
      <UiBadge variant="danger" dot>Необратимое действие</UiBadge>
      <p class="confirm-help">
        Для подтверждения введите точно: <code>{{ expectedConfirm }}</code>
      </p>
      <input v-model="deleteConfirm" class="input" type="text" :placeholder="expectedConfirm" autocomplete="off" />
    </section>

    <template #footer>
      <UiButton variant="ghost" :disabled="submitting" @click="modals.close">Отмена</UiButton>
      <UiButton
        :variant="op === 'delete' ? 'danger' : 'primary'"
        :disabled="submitting || (op === 'delete' && !canDelete)"
        @click="apply"
      >
        {{ submitting ? 'Применяем…' : `Применить к ${payload.selectedCount} ${payload.entityType === 'link' ? 'ссылкам' : 'элементам'}` }}
      </UiButton>
    </template>
  </UiModal>
</template>

<style scoped>
.modal-title { font-size: 17px; letter-spacing: -0.01em; margin: 0 0 4px; }
.modal-sub { font-size: 13px; color: var(--fg-muted, #9aa5b1); line-height: 1.5; margin: 0; }
.modal-sub strong { color: var(--fg, #e6eaf0); font-weight: 600; }

.op-grid {
  display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 14px;
}
.op-card {
  display: flex; align-items: flex-start; gap: 10px; padding: 12px;
  background: var(--bg-card, #1a1f29);
  border: 1px solid var(--border, #2a3340);
  border-radius: 8px; cursor: pointer; transition: 0.15s;
}
.op-card input { position: absolute; opacity: 0; pointer-events: none; }
.op-card.selected { border-color: var(--accent, #4f8cff); box-shadow: 0 0 0 1px rgba(79,140,255,0.3); }
.op-card-danger.selected { border-color: #ff5d8f; box-shadow: 0 0 0 1px rgba(255,93,143,0.3); }
.op-icon {
  width: 32px; height: 32px; border-radius: 7px;
  display: grid; place-items: center; flex-shrink: 0;
}
.op-icon-blue   { background: rgba(79,140,255,0.12); color: #6f9eff; }
.op-icon-purple { background: rgba(179,102,255,0.12); color: #b366ff; }
.op-icon-orange { background: rgba(255,139,77,0.12); color: #ff8b4d; }
.op-icon-red    { background: rgba(255,93,143,0.12); color: #ff5d8f; }
.op-text { display: flex; flex-direction: column; gap: 3px; }
.op-title { font-size: 13.5px; font-weight: 600; color: var(--fg, #e6eaf0); }
.op-desc { font-size: 12px; color: var(--fg-muted, #9aa5b1); line-height: 1.4; }

.op-detail { padding-top: 8px; border-top: 1px solid var(--border, #2a3340); }
.op-detail.danger { display: flex; flex-direction: column; gap: 10px; }
.field-label { display: block; font-size: 13px; font-weight: 600; color: var(--fg, #e6eaf0); margin-bottom: 6px; }
.input {
  width: 100%; box-sizing: border-box;
  padding: 9px 12px; background: var(--bg, #0f1620);
  border: 1px solid var(--border, #2a3340); border-radius: 7px;
  color: var(--fg, #e6eaf0); font: inherit; font-size: 14px;
}
.input:focus { outline: none; border-color: var(--accent-hi, #6f9eff); }
.tag-chips { display: flex; flex-wrap: wrap; gap: 6px; }
.tag-chip {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 4px 10px; background: var(--bg, #0f1620);
  border: 1px solid var(--border, #2a3340); border-radius: 100px;
  color: var(--fg, #e6eaf0); font: inherit; font-size: 12.5px; cursor: pointer;
}
.tag-chip.selected { border-color: var(--accent-hi, #6f9eff); background: rgba(79,140,255,0.08); }
.tag-dot { width: 8px; height: 8px; border-radius: 50%; }
.tag-add { color: var(--fg-muted, #9aa5b1); }
.tag-add:disabled { opacity: 0.55; cursor: not-allowed; }

.archive-note { font-size: 13px; color: var(--fg-muted, #9aa5b1); line-height: 1.5; margin: 0; }

.confirm-help { font-size: 12px; color: var(--fg-muted, #9aa5b1); margin: 0; }
.confirm-help code {
  font-family: var(--font-mono, ui-monospace, monospace); font-size: 11.5px;
  background: var(--bg, #0f1620); padding: 1px 6px; border-radius: 4px;
  color: var(--accent-hi, #6f9eff);
}
</style>
